import {Button, Table} from 'antd';
import {useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';

import useIsCompact from 'utils/useIsCompact';
import {prettifyEnum} from 'utils/currencyFormatting';
import styles from '../../wallet.module.scss';

const emptyState = (
  <div className={styles.emptyState}>
    <h3>No mobile money operators</h3>
    <p>Countries you enable for mobile money will be listed here.</p>
  </div>
);

/** The operator logo and its supported providers are already fetched — show them. */
const CountryCell = ({record}) => (
  <div className={styles.currencyCell}>
    {record?.logo ? (
      <img
        className={styles.flag}
        src={record.logo}
        alt=""
        aria-hidden="true"
      />
    ) : (
      <span className={styles.currencyBadge}>
        {record?.currencyCode || '—'}
      </span>
    )}
    <span className={styles.currencyText}>
      <span className={styles.currencyCode}>
        {prettifyEnum(record?.countryName)}
      </span>
      <span className={styles.currencyName}>
        Settles in {record?.currencyCode}
      </span>
    </span>
  </div>
);

CountryCell.propTypes = {record: PropTypes.object};

/**
 * Some countries come back with the same operator listed more than once —
 * Cote d'Ivoire repeats MOOV and MTN, Cameroon repeats MTN and ORANGEMONEY.
 * Rendering "Mtn, Moov, Moov, Mtn" tells an admin nothing, so collapse to the
 * distinct set. (The duplication itself is in the source data.)
 */
const distinctCodes = providers =>
  (providers ?? [])
    .map(item => item?.providerCode)
    .filter((code, index, all) => code != null && all.indexOf(code) === index);

const ProviderChips = ({providers}) => {
  const codes = distinctCodes(providers);
  if (!codes.length) return <span className={styles.methodNone}>None</span>;
  return (
    <div className={styles.currencyChips}>
      {codes.map(code => (
        <span className={styles.chip} key={code}>
          {prettifyEnum(code)}
        </span>
      ))}
    </div>
  );
};

ProviderChips.propTypes = {providers: PropTypes.array};

const AllMobileMoneyOperators = ({data, loading, operations}) => {
  const navigate = useNavigate();
  const isCompact = useIsCompact();

  const goToOperator = record =>
    navigate(`/dashboard/wallet/mobile-money/${record?.countryName}`);

  const buildColumns = () => {
    const base = [
      {
        title: 'Country',
        key: 'countryName',
        fixed: 'left',
        width: 250,
        render: (_, record) => <CountryCell record={record} />,
      },
      {
        title: 'Currency',
        dataIndex: 'currencyCode',
        key: 'currencyCode',
        width: 110,
        render: value => <span className={styles.chip}>{value}</span>,
      },
      {
        title: 'Operators',
        key: 'supportedProviders',
        render: (_, record) => (
          <ProviderChips providers={record?.supportedProviders} />
        ),
      },
    ];

    if (operations?.update) {
      base.push({
        title: '',
        key: 'action',
        fixed: 'right',
        width: 110,
        render: (_, record) => (
          <Button size="small" onClick={() => goToOperator(record)}>
            Update
          </Button>
        ),
      });
    }
    return base;
  };

  const columns = buildColumns();

  if (isCompact) {
    return (
      <div className={styles.cardList}>
        {(data ?? []).map(record => (
          <div className={styles.card} key={record?.countryName}>
            <div className={styles.cardHeader}>
              <CountryCell record={record} />
              {operations?.update && (
                <Button size="small" onClick={() => goToOperator(record)}>
                  Update
                </Button>
              )}
            </div>
            <div className={styles.cardSection}>
              <span className={styles.expandedLabel}>Operators</span>
              <ProviderChips providers={record?.supportedProviders} />
            </div>
          </div>
        ))}
        {!loading && !(data ?? []).length && emptyState}
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <Table
        rowKey="countryName"
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{x: 720}}
        locale={{emptyText: emptyState}}
      />
    </div>
  );
};

AllMobileMoneyOperators.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  operations: PropTypes.object,
};

export default AllMobileMoneyOperators;
